'use client';

import { captureAnalyticsClientEvent } from '@/analytics/analytics-client';
import type { WorkspaceTaskBlockPaymentContext } from '@/cms/components/blocks/workspace/workspace-1/contracts/task-block-payment';
import { resolveProviderButtonLabel } from '@/components/checkout/checkout-button-copy';
import {
  type ProviderCheckoutButtonClasses,
  ProviderCheckoutButtons,
} from '@/components/checkout/provider-checkout-buttons';
import type { PaymentProviderId } from '@/payment/provider-list';
import type { CheckoutErrorCode } from '@/payment/services/checkout/types';
import type { AdminV2PaywallPopupBranchSlug } from '@/site-runtime/code-owned/paywall-branches';
import type { Locale } from 'next-intl';
import { useEffect } from 'react';
import type { SiteRuntimeCheckoutSurface } from './site-runtime-checkout-login-intent';
import { useSiteRuntimeHostedCheckout } from './use-site-runtime-hosted-checkout';

export function SiteRuntimeCheckoutProviderButtons({
  checkoutIntentKey,
  checkoutSurface = 'upgrade_dialog',
  conversionId,
  classes,
  getErrorLabel,
  dataAttributes,
  locale,
  onCheckoutCreated,
  paywallBranchSlug,
  onCheckoutError,
  onUnauthenticated,
  paypalButtonLabel,
  providerOptions,
  providerOptionsError,
  providerOptionsPending,
  redirectingLabel,
  stripeButtonLabel,
  taskBlockPayment,
  providerTone,
  unavailableLabel,
}: {
  checkoutIntentKey: string;
  checkoutSurface?: SiteRuntimeCheckoutSurface;
  conversionId?: string;
  classes: ProviderCheckoutButtonClasses;
  dataAttributes?: Readonly<Record<string, string>>;
  getErrorLabel: (errorCode: CheckoutErrorCode) => string;
  locale: Locale;
  onCheckoutCreated?: () => void;
  paywallBranchSlug?: AdminV2PaywallPopupBranchSlug;
  onCheckoutError: (errorCode: CheckoutErrorCode) => void;
  onUnauthenticated: (loginCallbackUrl: string) => void;
  paypalButtonLabel: string;
  providerOptions: readonly PaymentProviderId[] | null;
  providerOptionsError: CheckoutErrorCode | null;
  providerOptionsPending: boolean;
  redirectingLabel: string;
  stripeButtonLabel: string;
  taskBlockPayment?: WorkspaceTaskBlockPaymentContext;
  providerTone?: 'primary' | 'secondary';
  unavailableLabel: string;
}) {
  const checkout = useSiteRuntimeHostedCheckout({
    checkoutIntentKey,
    checkoutSurface,
    conversionId,
    getCheckoutErrorMessage: getErrorLabel,
    locale,
    onCheckoutCreated,
    paywallBranchSlug,
    onCheckoutError,
    onUnauthenticated,
    taskBlockPayment,
  });

  useEffect(() => {
    captureAnalyticsClientEvent('select_item', {
      checkout_intent_key: checkoutIntentKey,
      checkout_surface: checkoutSurface,
      conversion_id: checkout.conversionId,
      paywall_branch_slug: paywallBranchSlug ?? null,
      item_list_name: checkoutSurface,
      items: [{ item_id: checkoutIntentKey, item_name: checkoutIntentKey }],
    });
  }, [
    checkout.conversionId,
    checkoutIntentKey,
    checkoutSurface,
    paywallBranchSlug,
  ]);

  return (
    <ProviderCheckoutButtons
      activeProvider={checkout.activeProvider}
      classes={classes}
      dataAttributes={dataAttributes}
      onSelect={(provider: PaymentProviderId) => {
        void checkout.startCheckout(provider);
      }}
      paypalLabel={resolveProviderButtonLabel(paypalButtonLabel, 'PayPal')}
      providerOptions={providerOptions}
      providerOptionsPending={providerOptionsPending}
      redirectingLabel={redirectingLabel}
      stripeLabel={resolveProviderButtonLabel(stripeButtonLabel, 'Stripe')}
      providerTone={providerTone}
      submitting={checkout.submitting}
      unavailableLabel={
        providerOptionsError
          ? getErrorLabel(providerOptionsError)
          : unavailableLabel
      }
    />
  );
}
